import type { ColumnDef } from "@tanstack/react-table";
import type { AdminAuthorFileItem } from "@/types/article";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { formatDate } from "@/lib/format";
import { ArrowRight, RefreshCw } from "lucide-react";
import { Link } from "react-router-dom";

export type AuthorFileAction = "publish" | "sync";

export function getAuthorFilesColumns(
  onAction: (file: AdminAuthorFileItem, action: AuthorFileAction) => void
): ColumnDef<AdminAuthorFileItem>[] {
  return [
    {
      accessorKey: "title",
      header: "제목",
      cell: ({ row }) => (
        <div className="max-w-[300px] truncate text-sm font-medium" title={row.original.title}>
          {row.original.title}
        </div>
      ),
    },
    {
      accessorKey: "isPublished",
      header: "상태",
      cell: ({ row }) => {
        const { isPublished, hasUpdate } = row.original;
        if (!isPublished) {
          return (
            <Badge variant="outline" className="border-gray-300 bg-gray-50 text-gray-700">
              미생성
            </Badge>
          );
        }
        if (hasUpdate) {
          return (
            <Badge variant="default" className="border-amber-300 bg-amber-100 text-amber-800">
              업데이트 있음
            </Badge>
          );
        }
        return (
          <Badge variant="default" className="border-green-300 bg-green-100 text-green-800">
            발행됨
          </Badge>
        );
      },
    },
    {
      accessorKey: "updatedAt",
      header: "수정일",
      cell: ({ row }) => (
        <div className="whitespace-nowrap text-sm">{formatDate(row.original.updatedAt, { format: "short" })}</div>
      ),
    },
    {
      id: "actions",
      header: "작업",
      cell: ({ row }) => {
        const file = row.original;

        if (!file.isPublished) {
          return (
            <Button variant="outline" size="sm" asChild>
              <Link to={`/articles/publish/${file._id}`}>
                <ArrowRight className="mr-1 h-3 w-3" />
                생성하기
              </Link>
            </Button>
          );
        }

        if (file.hasUpdate) {
          return (
            <Button variant="outline" size="sm" onClick={() => onAction(file, "sync")}>
              <RefreshCw className="mr-1 h-3 w-3" />
              동기화
            </Button>
          );
        }

        if (file.articleId) {
          return (
            <Button variant="ghost" size="sm" asChild>
              <Link to={`/articles/${file.articleId}/edit`}>수정</Link>
            </Button>
          );
        }

        return null;
      },
    },
  ];
}
